import { useQuery } from "@apollo/client";
import { sortBy, uniqBy } from "lodash";
import { Fragment, useMemo } from "react";
import { Redirect } from "react-router-dom";
import {
  GetMyCareerOverviewQuery,
  GetMyCareerOverviewQueryVariables,
  MyCareerOverviewPersonFragment,
} from "types/graphql-schema";

import getMyCareerOverviewQuery from "@apps/org-settings/graphql/get-my-career-overview-query";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import SettingsPageHeader from "../settings-page-header";
import {
  getRolesAndCompetenciesBreadcrumbs,
  personCareerPath,
} from "./helpers";
import PersonRoleEmptyState from "./person-role-empty-state";

type Person = MyCareerOverviewPersonFragment;
type Section = {
  key: string;
  title: string;
  rows: { person: Person; role: Person["currentRoles"][number] | null }[];
};

const cellClassName = "px-4 py-2 border-b";

// Career Tracks for someone who doesn't run the ladder: the tracks they and
// their direct reports are on, and the people on each. Each person opens on
// their own view; whole tracks are for admins. Someone with no reports on a
// single track has nothing to pick between, so they go straight to theirs;
// with no role at all, there's nothing to show them but that.
const MemberCareerOverview = ({
  organizationId,
}: {
  organizationId: number;
}) => {
  const label = useLabel();
  const link = useLink();
  const { data, loading } = useQuery<
    GetMyCareerOverviewQuery,
    GetMyCareerOverviewQueryVariables
  >(getMyCareerOverviewQuery, { fetchPolicy: "network-only" });

  const sections = useMemo(() => {
    const me = data?.me;
    if (!me) {
      return [];
    }
    // You first, then your reports by name.
    const people = [
      me,
      ...sortBy(assertEdgesNonNull(me.directReports), (person) =>
        person.name.toLowerCase(),
      ),
    ];
    const byTrack = new Map<number, Section>();
    const withoutRole: Section = {
      key: "none",
      title: "No career track yet",
      rows: [],
    };
    people.forEach((person) => {
      if (person.currentRoles.length === 0) {
        withoutRole.rows.push({ person, role: null });
      }
      person.currentRoles.forEach((role) => {
        const section = byTrack.get(role.careerTrack.id) ?? {
          key: `track-${role.careerTrack.id}`,
          title: role.careerTrack.title,
          rows: [],
        };
        section.rows.push({ person, role });
        byTrack.set(role.careerTrack.id, section);
      });
    });
    return [
      ...sortBy([...byTrack.values()], (section) =>
        section.title.toLowerCase(),
      ),
      ...(withoutRole.rows.length > 0 ? [withoutRole] : []),
    ];
  }, [data]);

  const me = data?.me;
  const hasOnlyOwnTrack =
    !!me &&
    me.directReports.edges.length === 0 &&
    uniqBy(me.currentRoles, (role) => role.careerTrack.id).length === 1;
  if (me && hasOnlyOwnTrack) {
    return <Redirect to={personCareerPath(organizationId, me.id)} />;
  }
  const hasNothingToShow =
    !!me && me.directReports.edges.length === 0 && me.currentRoles.length === 0;

  return (
    <div>
      <SettingsPageHeader
        breadcrumbs={getRolesAndCompetenciesBreadcrumbs(organizationId, label)}
      />
      {loading && !data ? (
        <div className="flex justify-center mt-8">
          <Loading />
        </div>
      ) : me && hasNothingToShow ? (
        <PersonRoleEmptyState
          organizationId={organizationId}
          person={me}
          canManageTracks={false}
          isSelf
        />
      ) : (
        <>
          <p className="mb-4 text-sm text-gray-500">
            {me && me.directReports.edges.length > 0
              ? "Your career track, and your direct reports'. Open someone to see what's expected of them."
              : "Your career tracks. Open one to see what's expected of you."}
          </p>
          <div className="w-full rounded-md border overflow-hidden bg-white">
            <table
              className="w-full text-sm table-fixed"
              aria-label="My career tracks"
            >
              <thead className="text-xs font-medium text-gray-500">
                <tr className="bg-gray-100">
                  <th className={classNames(cellClassName, "text-left w-1/2")}>
                    Person
                  </th>
                  <th className={classNames(cellClassName, "text-left")}>
                    Role
                  </th>
                  <th className={classNames(cellClassName, "text-left w-32")}>
                    Level
                  </th>
                </tr>
              </thead>
              <tbody>
                {sections.map((section) => (
                  <Fragment key={section.key}>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={3}
                        className={classNames(
                          cellClassName,
                          "font-semibold text-gray-800",
                        )}
                      >
                        {section.title}
                      </td>
                    </tr>
                    {section.rows.map(({ person, role }) => {
                      // Without a role there's no table to open.
                      const open = role
                        ? () =>
                            link.redirect(
                              personCareerPath(organizationId, person.id),
                            )
                        : undefined;
                      return (
                        <tr
                          key={`${person.id}-${role?.id ?? "none"}`}
                          className={classNames(
                            open && "cursor-pointer hover:bg-gray-50",
                          )}
                          role={open ? "link" : undefined}
                          tabIndex={open ? 0 : undefined}
                          aria-label={
                            open
                              ? `My career tracks: Open ${person.name}`
                              : `My career tracks: ${person.name}`
                          }
                          onClick={open}
                          onKeyDown={(evt) => {
                            if (evt.key === "Enter") {
                              open?.();
                            }
                          }}
                        >
                          <td className={cellClassName}>
                            <div className="flex items-center gap-2 font-medium text-gray-800">
                              <Avatar user={person} size="5" />
                              {person.id === data?.me?.id
                                ? `${person.name} (you)`
                                : person.name}
                            </div>
                          </td>
                          <td
                            className={classNames(
                              cellClassName,
                              "text-gray-700",
                            )}
                          >
                            {role?.title ?? "–"}
                          </td>
                          <td
                            className={classNames(
                              cellClassName,
                              "text-gray-500",
                            )}
                          >
                            {role ? `Level ${role.level}` : "–"}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default MemberCareerOverview;
